<template>
  <button
    ref="btn"
    :type="type"
    :disabled="disabled"
    :class="[
      'magnetic-button relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full px-6 py-3 text-sm font-semibold transition will-change-transform focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-500/20 disabled:pointer-events-none disabled:opacity-50',
      variantClass
    ]"
    @pointermove="move"
    @pointerleave="leave"
  >
    <span
      ref="shine"
      class="button-shine pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300"
    ></span>

    <span class="relative inline-flex items-center gap-2">
      <span
        v-if="icon"
        class="material-symbols-outlined text-[17px]"
      >
        {{ icon }}
      </span>

      <slot />
    </span>
  </button>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  icon: { type: String, default: '' },
  variant: {
    type: String,
    default: 'primary',
    validator: value => ['primary', 'secondary', 'ghost'].includes(value)
  },
  type: { type: String, default: 'button' },
  disabled: { type: Boolean, default: false }
})

const btn = ref(null)
const shine = ref(null)

const variantClass = computed(() => {
  if (props.variant === 'secondary') {
    return 'secondary-variant'
  }

  if (props.variant === 'ghost') {
    return 'ghost-variant'
  }

  return 'primary-variant'
})

function move(event) {
  if (!btn.value || props.disabled) return

  const rect = btn.value.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top
  const dx = (x - rect.width / 2) / (rect.width / 2)
  const dy = (y - rect.height / 2) / (rect.height / 2)

  btn.value.style.transform =
    `perspective(700px) rotateX(${-dy * 4}deg) rotateY(${dx * 4}deg) translateY(-1px)`

  if (shine.value) {
    shine.value.style.opacity = '1'
    shine.value.style.background =
      `radial-gradient(circle at ${x}px ${y}px, rgba(255,255,255,.24), transparent 42%)`
  }
}

function leave() {
  if (!btn.value) return

  btn.value.style.transform =
    'perspective(700px) rotateX(0) rotateY(0) translateY(0)'

  if (shine.value) {
    shine.value.style.opacity = '0'
  }
}
</script>

<style scoped>
.magnetic-button {
  transform-style: preserve-3d;
  transition:
    transform 220ms ease,
    box-shadow 300ms ease,
    border-color 300ms ease,
    background-color 300ms ease;
}

.primary-variant {
  color: white;
  background:
    linear-gradient(
      135deg,
      #6d28d9,
      #9333ea 56%,
      #c026d3
    );
  box-shadow:
    0 10px 28px rgba(124, 58, 237, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.20);
}

.primary-variant:hover {
  box-shadow:
    0 16px 38px rgba(124, 58, 237, 0.30),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
}

.secondary-variant {
  color: var(--ce-strong);
  background: var(--ce-glass-strong);
  border: 1px solid var(--ce-border);
  box-shadow: var(--ce-shadow-soft);
  backdrop-filter: blur(18px);
}

.secondary-variant:hover {
  border-color: var(--ce-border-strong);
}

.ghost-variant {
  color: var(--ce-muted);
  background: transparent;
  border: 1px solid transparent;
}

.ghost-variant:hover {
  color: var(--ce-strong);
  background: var(--ce-glass);
  border-color: var(--ce-border);
}
</style>
